import { useState } from 'react';
import { Alert, App, Button, Card, Modal, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { DoohMedia } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { useAuth } from '@/features/auth/context/AuthContext';
import { canManageDoohMedia } from '@/layouts/navigation';
import { doohApi } from '../api/doohApi';
import { MediaLibraryTable } from '../components/MediaLibraryTable';
import { MediaUploadForm } from '../components/MediaUploadForm';
import { MediaDetailDrawer } from '../components/MediaDetailDrawer';
import { DoohFlowHint } from '../components/DoohFlowHint';
import { useDoohMedia } from '../hooks/useDoohMedia';

/**
 * Biblioteca de medios DOOH (TG-58, `/dooh/medios`): listar, subir JPG/MP4, ver
 * detalle y eliminar. Solo SUPER_ADMIN / MARKETING (el guard de ruta lo exige; aquí
 * se ocultan las acciones y se avisa si el rol no tiene permiso).
 */
export function MediaLibraryPage() {
  const { user } = useAuth();
  const { message } = App.useApp();
  const library = useDoohMedia();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selected, setSelected] = useState<DoohMedia | null>(null);

  const canManage = canManageDoohMedia(user?.role);

  const upload = async (file: File) => {
    try {
      await doohApi.uploadMedia(file);
      message.success('Medio subido a la biblioteca.');
      setUploadOpen(false);
      library.refresh();
    } catch (caught) {
      message.error(caught instanceof ApiError ? caught.message : 'No se pudo subir el medio.');
    }
  };

  const remove = async (media: DoohMedia) => {
    try {
      await doohApi.removeMedia(media.id);
      message.success('Medio eliminado.');
      setSelected(null);
      library.refresh();
    } catch (caught) {
      // 409 DOOH_MEDIA_IN_USE: el medio pertenece a la emisión activa.
      message.error(caught instanceof ApiError ? caught.message : 'No se pudo eliminar el medio.');
    }
  };

  return (
    <div className="dooh-page">
      <div className="dooh-page__header">
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Biblioteca de medios
          </Typography.Title>
          <Typography.Text type="secondary">
            Sube las imágenes y los videos que quieres emitir en las pantallas de toda la red.
          </Typography.Text>
        </div>
        {canManage && (
          <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
            Subir medio
          </Button>
        )}
      </div>

      <DoohFlowHint current="media" />

      {!canManage && (
        <Alert
          type="warning"
          showIcon
          message="Solo lectura"
          description="Tu rol no permite subir ni eliminar medios. Se requiere SuperAdmin o Marketing."
        />
      )}

      <Card>
        <MediaLibraryTable
          media={library.data}
          loading={library.loading}
          error={library.error}
          canManage={canManage}
          onRetry={library.refresh}
          onUpload={() => setUploadOpen(true)}
          onSelect={setSelected}
          onDelete={(media) => void remove(media)}
        />
      </Card>

      <Modal
        title="Subir medio"
        open={uploadOpen}
        onCancel={() => setUploadOpen(false)}
        footer={null}
        destroyOnClose
      >
        <MediaUploadForm onUpload={upload} />
      </Modal>

      <MediaDetailDrawer
        media={selected}
        open={selected !== null}
        canManage={canManage}
        onClose={() => setSelected(null)}
        onDelete={(media) => void remove(media)}
      />
    </div>
  );
}
